'use client';
import {
	noop,
	useIntl,
	type UserMalaysiaDomesticRinggitBorrowingUserProvidedValues,
	type UserRequirement,
	useSettings
} from '@capsicum/common';
import { useLatestAcceptedAgreement } from '@capsicum/features/account/shared';
import { useCallback, useMemo, useState } from 'react';

import { useSubmitRequirementStateByIdentifierQuery } from '../data-access/queries/useRequirementsQueries';

enum InvestedOptions {
	LESS = 'less',
	MORE = 'more'
}

enum DomesticBorrowingState {
	WITH = 'with',
	WITHOUT = 'without'
}

type DomesticBorrowing = {
	id: DomesticBorrowingState;
	label: string;
};

type UseDomesticRinggitBorrowingProps = {
	requirement: UserRequirement;
};

export const useDomesticRinggitBorrowing = ({ requirement }: UseDomesticRinggitBorrowingProps) => {
	const { $t } = useIntl();
	const { isLoading, isSuccess, mutate } = useSubmitRequirementStateByIdentifierQuery();

	const { settings } = useSettings();
	const { latestAcceptedAgreementLink } = useLatestAcceptedAgreement();

	const [currentStep, setCurrentStep] = useState(0);

	const [domesticRinggitBorrowingSelected, setDomesticRinggitBorrowingSelected] = useState<DomesticBorrowing | null>(
		null
	);
	const domesticRinggitBorrowingOptions = useMemo(
		() => [
			{
				id: DomesticBorrowingState.WITHOUT,
				label: $t('domesticRinggitBorrowing.modal.option1'),
				testID: 'domestic-ringgit-borrowing-no'
			},
			{
				id: DomesticBorrowingState.WITH,
				label: $t('domesticRinggitBorrowing.modal.option2'),
				testID: 'domestic-ringgit-borrowing-yes'
			}
		],
		[$t]
	);

	const [selectedInvested, setSelectedInvested] = useState<null | string>(null);

	const onSelectDomesticRinggitBorrowing = useCallback((item: DomesticBorrowing) => {
		const selectedOption = domesticRinggitBorrowingOptions.find(option => option.id === item.id);

		if (selectedOption) {
			setDomesticRinggitBorrowingSelected(selectedOption);
		}
	}, []);

	const hasDomesticRinggitBorrowing = domesticRinggitBorrowingSelected?.id === DomesticBorrowingState.WITH;

	const investedOptions = useMemo(
		() => [
			{
				id: InvestedOptions.LESS,
				label: $t('domesticRinggitBorrowing.investedOption1'),
				testId: `domestic-ringgit-borrowing-radio-button-${InvestedOptions.LESS}`
			},
			{
				id: InvestedOptions.MORE,
				label: $t('domesticRinggitBorrowing.investedOption2'),
				testId: `domestic-ringgit-borrowing-radio-button-${InvestedOptions.MORE}`
			}
		],
		[$t]
	);
	const investedMoreThanRM1Million = selectedInvested === InvestedOptions.MORE;
	const [isDeclarationConfirmed, setIsDeclarationConfirmed] = useState(false);

	const policyNoticeLink = settings?.bankOfNegaraLink as string;

	const disabledFirstButton = useMemo(() => {
		switch (currentStep) {
			case 0:
				return domesticRinggitBorrowingSelected === null;
			case 1:
				return !isDeclarationConfirmed || (hasDomesticRinggitBorrowing ? selectedInvested == null : false);
			default:
				return false;
		}
	}, [
		currentStep,
		hasDomesticRinggitBorrowing,
		isDeclarationConfirmed,
		domesticRinggitBorrowingSelected,
		selectedInvested
	]);

	const onSubmitStatement = useCallback(
		(onSuccess = noop) => {
			const payload: UserMalaysiaDomesticRinggitBorrowingUserProvidedValues = {
				hasDomesticRinggitBorrowing
			};

			if (hasDomesticRinggitBorrowing === true) {
				payload.investedMoreThanRM1Million = investedMoreThanRM1Million;
			}

			mutate({ requirementData: payload, requirementIdentifier: requirement.identifier }, { onSuccess });
		},
		[investedMoreThanRM1Million, hasDomesticRinggitBorrowing, mutate, requirement.id]
	);

	return {
		currentStep,
		disabledFirstButton,
		domesticRinggitBorrowingOptions,
		domesticRinggitBorrowingSelected,
		domesticRinggitBorrowingTextSelected: domesticRinggitBorrowingSelected?.label || '',
		hasDomesticRinggitBorrowing,
		investedOptions,
		isDeclarationConfirmed,
		isLoading: isSuccess || isLoading,
		isSuccess,
		latestAcceptedAgreementLink,
		onSelectDomesticRinggitBorrowing,
		onSubmitStatement,
		policyNoticeLink,
		selectedInvested,
		setCurrentStep,
		setIsDeclarationConfirmed,
		setSelectedInvested
	};
};
